import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, ENTITY_TYPE_LABELS, OWNER_LABELS, entityRenewal } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Bank, PencilSimple, Trash, CaretRight, Flag } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", type: "llc", owner_party: "joint", notes: "" };

export default function Entities() {
  const { hid, isOperator, ownerLabels, ownerLabel, refreshEntities } = useHousehold();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => { if (hid) api.get(`/entities?household_id=${hid}`).then((r) => setItems(r.data)).catch(() => {}); }, [hid]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Entity name required"); return; }
    try {
      if (editId) await api.put(`/entities/${editId}?household_id=${hid}`, form);
      else await api.post(`/entities?household_id=${hid}`, form);
      toast.success("Saved"); setOpen(false); load(); refreshEntities();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this entity?")) return; await api.delete(`/entities/${id}?household_id=${hid}`); load(); refreshEntities(); };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Structure</p><h1 className="font-display text-4xl font-black tracking-tighter mt-1">Legal Entities</h1></div>
        {isOperator && <Button data-testid="add-entity-button" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="rounded-sm font-semibold"><Plus size={16} weight="bold" className="mr-1" /> Add Entity</Button>}
      </div>

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center"><Bank size={40} className="mx-auto text-muted-foreground mb-4" /><p className="font-display text-xl font-bold">No entities yet</p><p className="text-sm text-muted-foreground mt-1">Add the LLCs, trusts, and individuals that own your assets.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((e) => {
            const rn = entityRenewal(e.renewal_date, e.type);
            return (
            <div
              key={e.id}
              data-testid={`entity-card-${e.id}`}
              onClick={() => navigate(`/entities/${e.id}`)}
              className="bg-card border border-border rounded-sm p-5 group cursor-pointer hover:shadow-[0_8px_30px_hsl(var(--ploy-ink)/0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200"
            >
              <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-secondary px-2 py-1 rounded-sm">{ENTITY_TYPE_LABELS[e.type] || e.type}</span>
                {isOperator ? (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`edit-entity-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setForm({ ...empty, ...e }); setEditId(e.id); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><PencilSimple size={15} /></button>
                    <button data-testid={`delete-entity-${e.id}`} onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                  </div>
                ) : <CaretRight size={15} className="text-muted-foreground" />}
              </div>
              <h3 className="font-display text-lg font-bold mt-3 tracking-tight flex items-center gap-2">
                {rn.due && <span data-testid={`entity-alert-${e.id}`} title="Renewal due soon" className="h-2.5 w-2.5 rounded-full bg-destructive shrink-0" />}
                {e.name}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">{ownerLabel(e.owner_party)}</p>
              {rn.has ? (
                <p className={`text-xs mt-2 inline-flex items-center gap-1.5 ${rn.due ? "text-destructive font-semibold" : "text-muted-foreground/70"}`}>
                  <Flag size={13} weight={rn.due ? "fill" : "regular"} />
                  {rn.due ? `Renewal due in ${rn.days} day${rn.days === 1 ? "" : "s"}` : `Renews in ${rn.days} days`}
                </p>
              ) : rn.missing && (
                <p className="text-xs mt-2 inline-flex items-center gap-1.5 text-destructive font-semibold">
                  <Flag size={13} weight="fill" /> No renewal date — not being tracked
                </p>
              )}
              {e.notes && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{e.notes}</p>}
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">{editId ? "Edit Entity" : "New Entity"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Name</Label><Input data-testid="entity-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Foyco Holdings LLC" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Type</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger data-testid="entity-type-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ENTITY_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Owner</Label><Select value={form.owner_party} onValueChange={(v) => setForm({ ...form, owner_party: v })}><SelectTrigger data-testid="entity-owner-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ownerLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="save-entity-button" onClick={save} className="rounded-sm font-semibold w-full">{editId ? "Save Changes" : "Add Entity"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
